import React from 'react';

export type VarianceSeverity = 'high' | 'medium' | 'low';

type DeliveryVarianceHighlightProps = {
  actual: number;
  planned: number;
  formattedDifference: string;
};

export function DeliveryVarianceHighlight({
  actual,
  planned,
  formattedDifference
}: DeliveryVarianceHighlightProps) {
  if (actual === planned) {
    return <span className="text-lg font-semibold text-[#181d1f]">On plan</span>;
  }

  const severity = getVarianceSeverity(actual, planned);
  const label = actual > planned ? 'overdelivered' : 'underdelivered';
  const styles = severityStyles[severity];

  return (
    <span className={`inline-flex items-center gap-1 rounded px-1 ${styles.container}`}>
      <span className={`text-lg font-semibold leading-[26px] ${styles.number}`}>
        {formattedDifference}
      </span>
      <span className={`text-xs font-normal leading-[20px] ${styles.label}`}>{label}</span>
    </span>
  );
}

type BudgetVarianceHighlightProps = {
  actual: number;
  planned: number;
  formattedDifference: string;
};

export function BudgetVarianceHighlight({
  actual,
  planned,
  formattedDifference
}: BudgetVarianceHighlightProps) {
  if (actual === planned) {
    return <span className="text-lg font-semibold text-[#181d1f]">On budget</span>;
  }

  const isUnderSpent = actual < planned;
  const label = isUnderSpent ? 'underspent' : 'overspent';
  const styles = isUnderSpent ? budgetStyles.underspent : budgetStyles.overspent;

  return (
    <span className={`inline-flex items-center gap-1 rounded px-1 ${styles.container}`}>
      <span className={`text-lg font-semibold leading-[26px] ${styles.text}`}>
        {formattedDifference}
      </span>
      <span className={`text-xs font-normal leading-[20px] ${styles.text}`}>{label}</span>
    </span>
  );
}

export function SeverityHighlightValue({
  value,
  actual,
  planned,
  suffix
}: {
  value: string;
  actual: number;
  planned: number;
  suffix?: string;
}) {
  const severity = getVarianceSeverity(actual, planned);
  const styles = severityStyles[severity];

  return (
    <span
      className={
        severity === 'low'
          ? 'inline-flex items-center gap-1'
          : `inline-flex items-center gap-1 rounded-md px-2 py-0.5 ${styles.container}`
      }>
      <span
        className={
          severity === 'low'
            ? 'text-lg font-semibold text-gray-800'
            : `text-lg font-semibold ${styles.number}`
        }>
        {value}
      </span>
      {suffix ? <span className="text-xs font-normal">{suffix}</span> : null}
    </span>
  );
}

export function SpendSeverityHighlightValue({
  value,
  actual,
  planned,
  suffix
}: {
  value: string;
  actual: number;
  planned: number;
  suffix?: string;
}) {
  const isOverSpending = actual > planned;
  const isUnderSpending = actual < planned;
  const styles = isOverSpending
    ? spendSeverityStyles.over
    : isUnderSpending
      ? spendSeverityStyles.under
      : null;

  return (
    <span
      className={
        styles
          ? `inline-flex items-center gap-1 rounded-md px-2 py-0.5 ${styles.container}`
          : 'inline-flex items-center gap-1'
      }>
      <span
        className={
          styles ? `text-lg font-semibold ${styles.text}` : 'text-lg font-semibold text-gray-800'
        }>
        {value}
      </span>
      {suffix ? <span className="text-xs font-normal">{suffix}</span> : null}
    </span>
  );
}

export function getVarianceSeverity(actual: number, planned: number): VarianceSeverity {
  if (planned === 0) return 'low';
  const pctDiff = Math.abs(actual - planned) / planned;
  if (pctDiff >= 0.5) return 'high';
  if (pctDiff >= 0.2) return 'medium';
  return 'low';
}

const severityStyles = {
  high: { container: 'bg-red-100', number: 'text-red-900', label: 'text-red-900' },
  medium: { container: 'bg-yellow-100', number: 'text-yellow-800', label: 'text-yellow-900' },
  low: { container: '', number: '', label: '' }
};

const budgetStyles = {
  underspent: { container: 'bg-lime-100', text: 'text-lime-900' },
  overspent: { container: 'bg-red-100', text: 'text-red-900' }
};

const spendSeverityStyles = {
  over: { container: 'bg-red-100', text: 'text-red-900' },
  under: { container: 'bg-lime-100', text: 'text-lime-900' }
};
